import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { tilePoster } from "../library/posterSize.js";
import { useProgressiveList } from "../library/useProgressiveList.js";
import { useSearchParams } from "react-router";
import type { Episode, SeriesItem, SeriesSeason } from "@hybrid-iptv/types";
import { cn, EmptyState, ErrorState, Section, Spinner, useDialogFocus } from "@hybrid-iptv/ui";
import { ProviderSetupPrompt, isProviderNotConfigured } from "../components/ProviderSetupPrompt.js";
import { TrailerButton } from "../library/TrailerModal.js";
import { TitleDetailExtras } from "../library/TitleDetail.js";
import {
  episodeKey,
  tmdbImageUrl,
  useSeriesDetails,
  useTmdbDetails,
  useTvMatch,
} from "../library/vodHooks.js";
import { seriesTitleFields } from "../library/titleFields.js";
import { mergeTvmazeEpisodes, useTvmazeEpisodes } from "../library/tvmazeEpisodes.js";
import { popularSortAvailable, useOpenRelated } from "../library/relatedTitles.js";
import { useCastPhotos } from "../library/castPhotos.js";
import { usesTmdb } from "../discovery/rowSupplier.js";
import { useInView } from "../library/useInView.js";
import { formatRemaining, isResumable, useProgressStore } from "../library/progressStore.js";
import { useActiveProviderSourceId } from "../epg/api.js";
import { usePlayerStore } from "../player/playerStore.js";
import { FavoriteStar } from "../library/FavoriteStar.js";
import { FAVORITES_CATEGORY } from "../library/favoritesStore.js";
import { useFavoriteRefs } from "../library/useFavorites.js";
import { useCatalogGenres, useCatalogQuery } from "../library/useCatalogQuery.js";
import { useFavoritesStore } from "../library/favoritesStore.js";
import { episodeName, formatAirDate, formatRuntime, pickNextUp } from "../library/seriesEpisodes.js";
import type { CatalogQuery } from "../library/catalogQuery.js";
import { useSettled } from "../library/useSettled.js";
import { tileLetter } from "../library/tileLetter.js";
import { pictureName, qualityLabel, useMayNotPlay } from "../library/mediaFormat.js";
import { genreLabel, orderMovieGenres } from "../library/genreOrder.js";
import { useViewerCountry } from "../settings/countryStore.js";

type SortKey = "az" | "recent" | "popular";

/** Render cap — a provider catalog can be tens of thousands of titles. */
const DISPLAY_CAP = 300;

export function SeriesPage() {
  const sourceId = useActiveProviderSourceId();
  // Universal search hands off a series here as `/series?q=…`; pre-fill the
  // search box so the list is already narrowed to that title.
  const [searchParams] = useSearchParams();
  const [query, setQuery] = useState(() => searchParams.get("q") ?? "");
  // Open on the device: most popular first (owner, 2026-10-11), from the discovery files.
  const popularOrder = popularSortAvailable();
  const [sort, setSort] = useState<SortKey>(popularOrder ? "popular" : "az");
  const [genre, setGenre] = useState("all");
  const [selected, setSelected] = useState<SeriesItem | null>(null);

  const favoriteSeriesRefs = useFavoriteRefs("series");
  // Stage B: the catalog is queried, not held. In the APK native SQLite answers;
  // on the website the same query runs over the catalog the server sends.
  const genreList = useCatalogGenres(sourceId, "series");
  const genres = genreList.data ?? [];
  const country = useViewerCountry();
  const orderedGenres = useMemo(() => orderMovieGenres(genres, country), [genres, country]);
  // The grid follows the PAUSE in typing, not the keystroke — see the Movies
  // page, where the same three-letter word cost three full passes over 300
  // tiles before the first result arrived.
  const settledQuery = useSettled(query, 200);
  const catalogQuery = useMemo<CatalogQuery>(
    () => ({
      kind: "series",
      ...(genre === FAVORITES_CATEGORY ? { ids: favoriteSeriesRefs } : genre !== "all" ? { category: genre } : {}),
      ...(settledQuery ? { text: settledQuery } : {}),
      sort: sort === "az" ? "az" : sort === "popular" ? "popular" : "pos",
      limit: DISPLAY_CAP,
    }),
    [genre, favoriteSeriesRefs, settledQuery, sort],
  );
  const page = useCatalogQuery<SeriesItem>(sourceId, catalogQuery);
  const isLoading = page.isLoading || genreList.isLoading;
  const isError = page.isError || genreList.isError;
  const error = page.error ?? genreList.error;
  const total = page.data?.total ?? 0;
  const all = page.data?.items ?? [];
  // Painted a batch at a time on a TV (see useProgressiveList).
  const shown = useProgressiveList(all);

  const openTitle = useCallback((item: SeriesItem) => setSelected(item), []);

  // A phone opened a series (remote/PhoneRemoteBridge.tsx): /series?q=<title>&open=<id>.
  // The page may already be showing, so a new q is taken up here, not only at first.
  const queryParam = searchParams.get("q");
  const openId = searchParams.get("open");
  useEffect(() => {
    if (queryParam !== null) setQuery(queryParam);
  }, [queryParam]);
  const openedFromPhone = useRef<string | null>(null);
  useEffect(() => {
    if (!openId || openedFromPhone.current === openId) return;
    const item = all.find((series) => series.id === openId);
    if (!item) return;
    openedFromPhone.current = openId;
    openTitle(item);
  }, [openId, all, openTitle]);

  const details = useSeriesDetails(sourceId, selected?.providerId);
  const tvMatch = useTvMatch(selected?.rawTitle ?? "", selected?.tmdbId);
  const tvDetails = useTmdbDetails("tv", tvMatch.data?.match?.tmdbId);
  // The panel's own details (they come with the episodes) first; TMDB fills the rest (Section 26).
  const fields = seriesTitleFields(selected, details.data?.details, tvDetails.data);
  // What the panel leaves out of the episodes (plot, a real name, the episode's own picture)
  // from TVmaze, once the panel's own seasons are here: the panel's data always wins.
  const tvmazeEpisodes = useTvmazeEpisodes(selected, !!selected && !!details.data);
  const selectedPictures = selected as { posterUrl?: string; backdropUrl?: string } | null;
  const episodeDetails = useMemo(() => {
    if (!details.data || !tvmazeEpisodes.data) return details.data;
    const own = details.data as { posterUrl?: string; backdropUrl?: string };
    const seasons = mergeTvmazeEpisodes(details.data.seasons, tvmazeEpisodes.data, [
      selectedPictures?.posterUrl,
      selectedPictures?.backdropUrl,
      own.posterUrl,
      own.backdropUrl,
    ]);
    return seasons === details.data.seasons ? details.data : { ...details.data, seasons };
  }, [details.data, tvmazeEpisodes.data, selectedPictures?.posterUrl, selectedPictures?.backdropUrl]);

  // OK on a related poster opens that series when it is in the playlist; Back
  // goes to the one before, onto the poster picked (as MoviesPage).
  const [trail, setTrail] = useState<Array<{ item: SeriesItem; via: number }>>([]);
  const returnToPoster = useRef<number | null>(null);
  const relatedIds = useMemo(
    () => [...(tvDetails.data?.recommendations?.results ?? []), ...(tvDetails.data?.similar?.results ?? [])].map((r) => r.id),
    [tvDetails.data],
  );
  const relatedQuery = useMemo<CatalogQuery>(() => ({ kind: "series", tmdbIds: relatedIds, limit: 40 }), [relatedIds]);
  const relatedPage = useCatalogQuery<SeriesItem>(sourceId, relatedQuery, selected !== null && relatedIds.length > 0);
  const inPlaylist = useMemo(() => {
    const byTmdb = new Map<number, SeriesItem>();
    for (const item of relatedPage.data?.items ?? []) if (item.tmdbId && !byTmdb.has(item.tmdbId)) byTmdb.set(item.tmdbId, item);
    return byTmdb;
  }, [relatedPage.data]);
  // Open (Section 26): Similar and You May Also Like from the discovery files, playable titles only.
  const open = useOpenRelated<SeriesItem>(sourceId, "series", selected, !usesTmdb());
  // Open: Commons photos for the cast (Section 26 step 7), initials where there is none.
  const cast = useCastPhotos(sourceId, "series", selected, fields.cast, !usesTmdb());
  const openRelated = (tmdbId: number): boolean => {
    const item = inPlaylist.get(tmdbId) ?? open.byTmdb.get(tmdbId);
    if (!item || !selected) return false;
    setTrail((steps) => [...steps, { item: selected, via: tmdbId }]);
    setSelected(item);
    return true;
  };
  const closeDetail = () => {
    const last = trail.at(-1);
    if (!last) {
      setSelected(null);
      return;
    }
    setTrail(trail.slice(0, -1));
    returnToPoster.current = last.via;
    setSelected(last.item);
  };
  useEffect(() => {
    const id = returnToPoster.current;
    if (id === null || !selected) return;
    let tries = 0;
    const timer = setInterval(() => {
      const poster = document.querySelector<HTMLElement>(`[data-related-tmdb="${id}"]`);
      if (poster || ++tries > 40) {
        clearInterval(timer);
        returnToPoster.current = null;
        poster?.focus();
        poster?.scrollIntoView({ block: "nearest", inline: "nearest" });
      }
    }, 100);
    return () => clearInterval(timer);
  }, [selected, tvDetails.data]);

  async function handlePlayEpisode(episode: Episode) {
    const key = episodeKey(episode);
    const saved = useProgressStore.getState().get(key);
    const store = usePlayerStore.getState();
    store.setPlayingContent({ contentType: "episode", contentRef: key });
    const label = `${selected?.rawTitle ?? ""} S${episode.seasonNumber}E${episode.episodeNumber}`;
    useProgressStore.getState().remember(key, {
      title: label.trim(),
      posterUrl: episode.stillUrl,
      url: episode.streamUrl,
      // So Play next can find the following episode (library/episodeSequence.ts, player/UpNext.tsx).
      ...(selected?.providerId ? { series: { providerId: selected.providerId, title: selected.rawTitle } } : {}),
    });
    // Close the detail overlay BEFORE awaiting playback so the bottom-dock
    // player is visible immediately — even while the stream is still loading
    // (or if it fails), the user should see the player, not a modal over it.
    setSelected(null);
    setTrail([]);
    await store.playSource({ url: episode.streamUrl, type: "vod", title: label.trim(), artworkUrl: episode.stillUrl, ...(episode.media ? { media: episode.media } : {}) });
    if (isResumable(saved)) store.seek(saved!.positionSeconds);
  }

  return (
    <div className="page page--library">
      <Section title="Series">
        <div className="library-toolbar">
          <input
            className="library-search"
            type="search"
            placeholder="Search series…"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            aria-label="Search series"
          />
          <select className="library-select" value={sort} onChange={(event) => setSort(event.target.value as SortKey)} aria-label="Sort series">
            {popularOrder && <option value="popular">Most popular</option>}
            <option value="az">A–Z</option>
          </select>
          <select className="library-select" value={genre} onChange={(event) => setGenre(event.target.value)} aria-label="Filter by genre">
            <option value="all">All genres</option>
            <option value={FAVORITES_CATEGORY}>★ Favorites</option>
            {/* The same order and names as Movies: new first, then A to Z, the US's
                and no country before other countries (owner, 2026-10-09). */}
            {[...orderedGenres.newest, ...orderedGenres.rest].map((g) => (
              <option key={g} value={g}>{genreLabel(g)}</option>
            ))}
          </select>
          <span className="library-count">{total} series</span>
        </div>

        {isLoading && (
          <div className="library-grid library-grid--loading"><Spinner /> Loading series…</div>
        )}
        {isError && isProviderNotConfigured(error) && <ProviderSetupPrompt fullPage />}
        {isError && !isProviderNotConfigured(error) && (
          <ErrorState
            title="Couldn't load series"
            body="The VOD catalog failed to load. Check the source connection in Settings."
            onRetry={() => window.location.reload()}
          />
        )}
        {!isLoading && !isError && total === 0 && (
          <EmptyState
            icon="🎞️"
            title={genre === FAVORITES_CATEGORY ? "No favorite series yet" : "No series found"}
            body={
              genre === FAVORITES_CATEGORY
                ? "Press ★ on any series — in search or here — and it will show up under Favorites."
                : query
                  ? "Try a different search."
                  : "Your source has no series."
            }
          />
        )}
        {!isLoading && !isError && total > 0 && (
          <div className="library-grid library-grid--series">
            {total > DISPLAY_CAP && (
              <p className="library-cap-note">Showing {DISPLAY_CAP} of {total} — use search to narrow.</p>
            )}
            {shown.map((item) => (
              <SeriesTile key={item.id} item={item} onOpen={openTitle} />
            ))}
          </div>
        )}
      </Section>

      {selected && (
        <SeriesDetail
          // A new panel for each step, so each has its own Back entry (MoviesPage).
          key={trail.length}
          item={selected}
          details={episodeDetails}
          loading={details.isLoading}
          error={details.isError}
          headerPoster={fields.posterUrl}
          headerOverview={fields.overview}
          rating={fields.rating}
          year={fields.year}
          genres={fields.genres}
          cast={cast}
          recommendations={usesTmdb() ? tvDetails.data?.recommendations?.results : open.youMayLike}
          similar={usesTmdb() ? tvDetails.data?.similar?.results : open.similar}
          trailerUrl={fields.trailerUrl}
          onClose={closeDetail}
          onOpenRelated={openRelated}
          onPlayEpisode={(episode) => void handlePlayEpisode(episode)}
        />
      )}
    </div>
  );
}

interface SeriesDetailProps {
  item: SeriesItem;
  details: SeriesItem | undefined;
  loading: boolean;
  error: boolean;
  headerPoster?: string;
  headerOverview?: string;
  rating?: number;
  /** First aired. */
  year?: number;
  genres?: string[];
  cast?: Array<{ id: number; name: string; character?: string; profile_path?: string | null }>;
  recommendations?: Array<{ id: number; title: string; poster_path?: string | null; release_date?: string | null; vote_average?: number }>;
  similar?: Array<{ id: number; title: string; poster_path?: string | null; release_date?: string | null; vote_average?: number }>;
  trailerUrl?: string;
  onClose: () => void;
  onOpenRelated?: (tmdbId: number) => boolean;
  onPlayEpisode: (episode: Episode) => void;
}

function SeriesDetail({ item, details, loading, error, headerPoster, headerOverview, rating, year, genres, cast, recommendations, similar, trailerUrl, onClose, onOpenRelated, onPlayEpisode }: SeriesDetailProps) {
  const [season, setSeason] = useState<number>(1);
  // An Xtream series loads its seasons on demand; an M3U playlist's arrive
  // with the catalog, and there is nothing further to ask for.
  const seasons = details?.seasons ?? item.seasons;
  const activeSeason = seasons.find((s) => s.seasonNumber === season) ?? seasons[0];
  const detailRef = useRef<HTMLDivElement>(null);
  // Focus goes in and stays in; Escape or a remote's Back closes it and focus
  // returns to the series it opened from — the same as the Movies detail
  // (TitleDetail). WHY (2026-09-26): without it Back went to the page behind
  // (or offered to leave the app) with this still open, and focus stayed on
  // the tile behind while the arrows were held inside — stuck. Paused while
  // the trailer plays, which has its own.
  const [trailerOpen, setTrailerOpen] = useState(false);
  useDialogFocus({ open: true, paused: trailerOpen, onClose, rootRef: detailRef });
  // The banner's first button: the episode to carry on with (Sparkle's "Play
  // Season 1 Episode 1", which follows the viewer). Re-read as progress saves.
  const progressEntries = useProgressStore((s) => s.entries);
  const nextUp = useMemo(
    () => pickNextUp(seasons, (key) => useProgressStore.getState().get(key)),
    // progressEntries: re-read on every save.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [seasons, progressEntries],
  );
  const seasonCount = seasons.length;
  // An Xtream series' episodes arrive after the page opens, so focus first
  // lands on the next button along; once they are here, Play takes it, unless
  // the viewer has already moved (onn box, 2026-10-07).
  const playRef = useRef<HTMLButtonElement>(null);
  const movedRef = useRef(false);
  const hasNextUp = nextUp !== null;
  useEffect(() => {
    if (!hasNextUp || movedRef.current) return;
    const active = document.activeElement;
    if (active && active !== playRef.current && detailRef.current?.contains(active)) playRef.current?.focus();
  }, [hasNextUp]);

  return (
    <div className="title-detail__overlay" onClick={onClose} role="presentation">
      <div
        ref={detailRef}
        className="title-detail"
        role="dialog"
        aria-modal="true"
        aria-label={item.rawTitle}
        tabIndex={-1}
        onClick={(event) => event.stopPropagation()}
        onKeyDownCapture={() => { movedRef.current = true; }}
      >
        <div className="title-detail__backdrop" style={headerPoster ? { backgroundImage: `url(${headerPoster})` } : undefined}>
          <div className="title-detail__backdrop-shade" />
        </div>
        <div className="title-detail__content">
          {headerPoster && <img className="title-detail__poster" src={headerPoster} alt={item.rawTitle} loading="lazy" />}
          <div className="title-detail__info">
            <h2 className="title-detail__title">{item.rawTitle}</h2>
            {(typeof rating === "number" && rating > 0 || year || seasonCount > 0 || (genres && genres.length > 0)) && (
              <div className="title-detail__meta">
                {typeof rating === "number" && rating > 0 && (
                  <span className="title-detail__rating" title="TMDB rating">★ {rating.toFixed(1)}</span>
                )}
                {year && <span>{year}</span>}
                {seasonCount > 0 && <span>{seasonCount === 1 ? "1 season" : `${seasonCount} seasons`}</span>}
                {genres && genres.length > 0 && <span className="title-detail__genres">{genres.slice(0, 3).join(" · ")}</span>}
              </div>
            )}
            {headerOverview && <p className="title-detail__overview">{headerOverview}</p>}
            <div
              className="title-detail__actions"
              // A frame later, as in TitleDetail: the remote's scrollIntoView cut
              // the smooth scroll short and left the banner half off the top.
              onFocusCapture={() => requestAnimationFrame(() => detailRef.current?.scrollTo({ top: 0, behavior: "smooth" }))}
              onKeyDown={(event) => {
                if (event.key === "ArrowUp") detailRef.current?.scrollTo({ top: 0, behavior: "smooth" });
              }}
            >
              {nextUp && (
                <button ref={playRef} type="button" className="ui-btn ui-btn--primary" onClick={() => onPlayEpisode(nextUp.episode)}>
                  {nextUp.label}
                </button>
              )}
              <SeriesFavoriteButton item={item} />
              <TrailerButton trailerUrl={trailerUrl} open={trailerOpen} onOpenChange={setTrailerOpen} />
              <button type="button" className="ui-btn ui-btn--ghost" onClick={onClose}>Close</button>
            </div>
          </div>
        </div>

        {/* The cast here; "You May Also Like" and "Similar Titles" go under the
            episodes: above them they looked like the end of the page and the
            episodes went unseen (owner, 2026-10-08). */}
        <TitleDetailExtras cast={cast} />

        <div className="series-episodes">
          {loading && <div className="series-episodes__note"><Spinner /> Loading seasons…</div>}
          {error && <div className="series-episodes__note">Couldn't load episodes for this series.</div>}
          {!loading && !error && seasons.length === 0 && (
            <div className="series-episodes__note">Source returned no episode data for this series.</div>
          )}
          {!loading && !error && seasons.length > 0 && (
            <>
              <div className="series-seasons" role="tablist" aria-label="Seasons">
                {seasons.map((s) => (
                  <button
                    key={s.seasonNumber}
                    type="button"
                    role="tab"
                    aria-selected={s.seasonNumber === activeSeason?.seasonNumber}
                    className={cn("series-seasons__tab", s.coverUrl && "series-seasons__tab--cover", s.seasonNumber === activeSeason?.seasonNumber && "series-seasons__tab--active")}
                    onClick={() => setSeason(s.seasonNumber)}
                    aria-label={seasonLabel(s)}
                  >
                    {/* "Season 2 · 2014 · 13 episodes" with the season's cover
                        (owner, 2026-10-07: from the provider's season info). */}
                    {s.coverUrl && <img className="series-seasons__cover" src={s.coverUrl} alt="" loading="lazy" decoding="async" />}
                    <span className="series-seasons__text">
                      <span className="series-seasons__name">Season {s.seasonNumber}</span>
                      <span className="series-seasons__facts">{seasonFacts(s)}</span>
                    </span>
                  </button>
                ))}
              </div>
              <ul className="series-episodes__list">
                {activeSeason?.episodes.map((episode) => (
                  <EpisodeRow key={episode.id} episode={episode} onPlay={() => onPlayEpisode(episode)} />
                ))}
              </ul>
            </>
          )}
        </div>

        <TitleDetailExtras recommendations={recommendations} similar={similar} onOpenRelated={onOpenRelated} />
      </div>
    </div>
  );
}

/**
 * One tile, split the same way as the Movies grid: a series that ships its own
 * artwork (nearly all of them — 10 of 8,175 lack it) needs no intersection
 * observer and no TMDB query, and 300 of each was what a full grid used to
 * build.
 */
const SeriesTile = memo(function SeriesTile({
  item,
  onOpen,
}: {
  item: SeriesItem;
  onOpen: (item: SeriesItem) => void;
}) {
  if (!item.posterUrl) return <SeriesTileNoArt item={item} onOpen={onOpen} />;
  return <SeriesCard item={item} onOpen={onOpen} artUrl={item.posterUrl} />;
});

/**
 * Artwork recovery for the few without: only once scrolled near, and only on a
 * match that clears MATCH_THRESHOLD, so an unrecognised title keeps its letter
 * tile rather than borrowing a wrong poster.
 */
function SeriesTileNoArt({ item, onOpen }: { item: SeriesItem; onOpen: (item: SeriesItem) => void }) {
  const [ref, inView] = useInView<HTMLDivElement>();
  const match = useTvMatch(item.rawTitle, item.tmdbId, inView);
  const artUrl = tmdbImageUrl(match.data.match?.summary.poster_path, "w342");
  return <SeriesCard item={item} onOpen={onOpen} artUrl={artUrl} wrapRef={ref} />;
}

function SeriesCard({
  item,
  onOpen,
  artUrl,
  wrapRef,
}: {
  item: SeriesItem;
  onOpen: (item: SeriesItem) => void;
  artUrl?: string;
  wrapRef?: React.RefObject<HTMLDivElement | null>;
}) {
  return (
    <div className="library-card-wrap" ref={wrapRef}>
      <button
        type="button"
        className="library-card library-card--series"
        onClick={() => onOpen(item)}
        aria-label={item.rawTitle}
      >
        {artUrl ? (
          <img className="library-card__art" src={tilePoster(artUrl)} alt={item.rawTitle} loading="lazy" decoding="async" />
        ) : (
          <span className={cn("library-card__fallback", item.categoryId)}>{tileLetter(item.rawTitle, "S")}</span>
        )}
        <span className="library-card__title">{item.rawTitle}</span>
      </button>
      <FavoriteStar kind="series" ref={item.id} label={item.rawTitle} className="fav-star--left" />
    </div>
  );
}

/** "2014 · 13 episodes" — the year it first aired, when known, and how many episodes there are to play. */
function seasonFacts(season: SeriesSeason): string {
  const count = season.episodes.length;
  return [season.airDate?.slice(0, 4), `${count} episode${count === 1 ? "" : "s"}`].filter(Boolean).join(" · ");
}

function seasonLabel(season: SeriesSeason): string {
  return `Season ${season.seasonNumber}, ${seasonFacts(season)}`;
}

/** The banner's favourite toggle, worded like Sparkle's "Add to favorites". */
function SeriesFavoriteButton({ item }: { item: SeriesItem }) {
  const isFavorite = useFavoritesStore((s) => s.isFavorite("series", item.id));
  const toggleFavorite = useFavoritesStore((s) => s.toggleFavorite);
  return (
    <button type="button" className="ui-btn ui-btn--ghost" aria-pressed={isFavorite} onClick={() => toggleFavorite("series", item.id)}>
      {isFavorite ? "★ In favorites" : "☆ Add to favorites"}
    </button>
  );
}

/**
 * One episode: its still, name, air date, length, rating and description, the
 * way the Sparkle clone showed them, in our vertical list (owner, 2026-10-07).
 * The whole row is one stop: OK plays it, or resumes it part-way through.
 */
function EpisodeRow({ episode, onPlay }: { episode: Episode; onPlay: () => void }) {
  const key = episodeKey(episode);
  const progress = useProgressStore((s) => s.get(key));
  const resumable = isResumable(progress);
  const [stillFailed, setStillFailed] = useState(false);
  const name = episodeName(episode);
  const facts = [
    formatAirDate(episode.airDate),
    formatRuntime(episode.runtimeMinutes),
    episode.rating ? `★ ${episode.rating.toFixed(1)}` : null,
    qualityLabel(episode.media),
  ].filter(Boolean);
  const mayNotPlay = useMayNotPlay(episode.media);
  const watched = progress && progress.durationSeconds > 0 ? Math.min(1, progress.positionSeconds / progress.durationSeconds) : 0;
  return (
    <li className="series-episode">
      <button
        type="button"
        className="series-episode__card"
        onClick={onPlay}
        aria-label={`${resumable ? "Resume" : "Play"} episode ${episode.episodeNumber}: ${name}`}
      >
        <span className="series-episode__still">
          {episode.stillUrl && !stillFailed ? (
            <img src={episode.stillUrl} alt="" loading="lazy" decoding="async" onError={() => setStillFailed(true)} />
          ) : (
            <span className="series-episode__still-fallback" aria-hidden>E{episode.episodeNumber}</span>
          )}
          <span className="series-episode__action" aria-hidden>{resumable ? "▶ Resume" : "▶ Play"}</span>
          {watched > 0 && (
            <span className="series-episode__bar" aria-hidden><span style={{ width: `${watched * 100}%` }} /></span>
          )}
        </span>
        <span className="series-episode__body">
          <span className="series-episode__title">
            <span className="series-episode__num">E{episode.episodeNumber}</span> {name}
          </span>
          {(facts.length > 0 || (resumable && progress)) && (
            <span className="series-episode__facts">
              {facts.join(" · ")}
              {resumable && progress && <span className="series-episode__progress">{facts.length > 0 ? " · " : ""}{formatRemaining(progress)}</span>}
            </span>
          )}
          {mayNotPlay && episode.media && (
            <span className="series-episode__warning">May not play on this device: its picture is {pictureName(episode.media)}.</span>
          )}
          {episode.overview && <span className="series-episode__overview">{episode.overview}</span>}
        </span>
      </button>
    </li>
  );
}
